import { motion, type Variants } from "motion/react";
import { useMemo } from "react";
import { cn } from "@/lib/utils";

/** Reads --primary-glow CSS variable (RGB triplet) for the drop-shadow effect */
function useGlowColor() {
	return useMemo(() => {
		if (typeof window === "undefined") return "16 185 129";
		const value = getComputedStyle(document.documentElement)
			.getPropertyValue("--primary-glow")
			.trim();
		return value || "16 185 129";
	}, []);
}

interface SlapLogoProps {
	className?: string;
	width?: number;
	/**
	 * Controls whether the animation plays.
	 * Useful if you want to trigger it manually or keep it in a static state.
	 */
	animate?: boolean;
}

function SlapLogo({
	className = "",
	width = 240,
	animate = true,
}: SlapLogoProps) {
	const glowColor = useGlowColor();
	// viewBox is 572x192, aspect ratio ~2.98:1
	const height = Math.round(width / (572 / 192));

	// Variants for the container to stagger the drawing of children paths
	const containerVariants: Variants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.15, // Delay between each letter starting to draw
				delayChildren: 0.2, // Initial delay before starting
			},
		},
	};

	// Variants for each path:
	// 1. Draw the outline (stroke)
	// 2. Fade in the fill color
	const pathVariants: Variants = {
		hidden: {
			pathLength: 0,
			fillOpacity: 0,
			strokeOpacity: 0,
		},
		visible: {
			pathLength: 1,
			fillOpacity: 1,
			strokeOpacity: 1,
			transition: {
				pathLength: { duration: 1.5, ease: "easeInOut" }, // Draw taking 1.5s
				fillOpacity: { duration: 0.8, ease: "easeIn", delay: 0.8 }, // Fade in fill after outline is mostly done
				strokeOpacity: { duration: 0.2 }, // Quickly show stroke at start
			},
		},
	};

	// We add a 'float' animation to the whole SVG after it's drawn
	const floatingVariants: Variants = {
		float: {
			y: [0, -10, 0],
			filter: [
				`drop-shadow(0 0 0px rgba(${glowColor}, 0))`,
				`drop-shadow(0 0 15px rgba(${glowColor}, 0.5))`,
				`drop-shadow(0 0 0px rgba(${glowColor}, 0))`,
			],
			transition: {
				duration: 4,
				ease: "easeInOut",
				repeat: Number.POSITIVE_INFINITY,
				repeatType: "reverse" as const,
				delay: 2.5, // Start floating after drawing is done
			},
		},
	};

	return (
		<motion.svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 572 192"
			width={width}
			height={height}
			className={cn("overflow-visible", className)}
			initial={animate ? "hidden" : false}
			animate={animate ? ["visible", "float"] : false}
			variants={{ ...containerVariants, ...floatingVariants }}
		>
			<title>Slap Logo</title>

			{/* Path 1: The 'L' shape */}
			<motion.path
				d="m174.4 153.9v-153.7h-37.5v191.2h115.9l11.5-37.5z"
				variants={pathVariants}
				stroke="currentColor"
				strokeWidth="2"
				fill="currentColor"
			/>

			{/* Path 2: The 'A' shape */}
			<motion.path
				fillRule="evenodd"
				d="m371 0.2l58.5 191.2h-39.2l-13.6-44.5h-63.1l-13.6 44.5h-39.3l58.5-191.2zm-5.8 109.2l-20.1-65.6-20.1 65.6z"
				variants={pathVariants}
				stroke="currentColor"
				strokeWidth="2"
				fill="currentColor"
			/>

			{/* Path 3: The 'P' shape */}
			<motion.path
				fillRule="evenodd"
				d="m570.8 64.2c0 35.3-28.6 64-63.9 64h-33v63.2h-37.5v-191.2h70.5c35.3 0 63.9 28.7 63.9 64zm-37.5 0c0-14.6-11.8-26.5-26.4-26.5h-33v52.9h33c14.6 0 26.4-11.8 26.4-26.4z"
				variants={pathVariants}
				stroke="currentColor"
				strokeWidth="2"
				fill="currentColor"
			/>

			{/* Path 4: The 'S' shape */}
			<motion.path
				d="m0.5 57.4c0 14.8 5.6 28.3 14.8 38.4 10.5 11.5 25.6 18.8 42.3 18.8h14.1c10.9 0 19.7 8.8 19.7 19.6 0 10.9-8.8 19.7-19.7 19.7h-71.2v37.5h69.8q0.7 0 1.4 0 0.8 0 1.5 0h0.3c30.7-0.9 55.4-26.2 55.4-57.2 0-14.8-5.6-28.2-14.8-38.4-10.5-11.5-25.6-18.8-42.4-18.8h-14.1c-10.8 0-19.6-8.8-19.6-19.6 0-10.9 8.8-19.7 19.6-19.7h71.3v-37.5h-71.3c-31.5 0-57.1 25.6-57.1 57.2z"
				variants={pathVariants}
				stroke="currentColor"
				strokeWidth="2"
				fill="currentColor"
			/>

			{/* Path 5: Small dot/detail */}
			<motion.path
				d="m284.6 10.6q0-0.2 0-0.2 0 0 0 0.2z"
				variants={pathVariants}
				stroke="currentColor"
				strokeWidth="2"
				fill="currentColor"
			/>

			{/* Path 6: Accent swoosh */}
			<motion.path
				d="m263.2 41.9c-29.3 3.8-39.8 25.5-39.8 25.5 62.2 15.1 61.4-52.6 61.2-56.8 0.1 1.9-0.6 17.9-34 20.5-33.9 2.6-28.5 29.6-27.9 32.2 7.3-14.4 25-21.6 40.5-21.4z"
				variants={pathVariants}
				stroke="currentColor"
				strokeWidth="2"
				fill="currentColor"
			/>
		</motion.svg>
	);
}

export { SlapLogo };
